import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, InputNumber, Select, Tag, Tooltip, Collapse } from 'antd'
import {
    fetchStatus,
    fetchUsbDevices,
    setUsbDevice,
    startStatusPolling,
    stopStatusPolling,
    checkDaemonCompatibility,
    store } from './store/store'
import { getLocalStorageValue, setLocalStorageValue } from './store/localstorage'
import { isTrainingBlocked } from './store/daemonCompatibility'
import { DaemonBlockBanner, DaemonDownloadControl } from './DaemonBlockUi'

const CONNECTIVITY_PORT_KEY = 'trainer/connectivityPort'

const STATE_TOOLTIPS = {
    'Uninitialized': 'Контроллер не инициализирован: USB-устройство пока не открыто и не настроено.',
    'Ready': 'USB-устройство открыто и готово к работе, приём данных ещё не идёт.',
    'Scanning': 'Идёт поиск датчиков.',
    'Receiving': 'Идёт приём данных с датчиков.'
}

// Explains each status marker and gives a short fix. Matched against the string
// codes that the local service returns (`ApiHandlers::errorName`/`stateName`).
const ERROR_TOOLTIPS = {
    'Ok': 'Ошибок нет.',
    'DeviceNotFound': 'Устройство не найдено: выбранное устройство не обнаружено или его драйвер не открывается. Проверьте подключение и драйвер (см. «Настройка USB-драйвера»).',
    'DeviceBusy': 'Устройство занято другим процессом или драйвером. Закройте программы, использующие его, и обновите статус.',
    'DeviceNotSupported': 'Устройство не подходит: не найден подходящий ANT-интерфейс. Используйте совместимый ANT-адаптер (Dynastream 0fcf:1004/1008/1009).',
    'DriverNotFound': 'Не удаётся открыть USB-устройство — установлен несовместимый драйвер или нет прав доступа. Настройте драйвер: см. «Настройка USB-драйвера».',
    'NoConnection': 'Нет связи с устройством: оно отключено или повреждено.',
    'Timeout': 'Тайм-аут передачи данных. Повторите попытку.',
    'TransferFail': 'Ошибка передачи данных по USB.',
    'InvalidArg': 'Некорректные аргументы запроса.',
    'FailToOpenChannel': 'Не удалось открыть канал для датчика.',
    'MaxChannelLimit': 'Достигнут предел числа каналов.'
}

function TipTag({ tip, ...tagProps }) {
    if (!tip) return <Tag { ...tagProps } />
    return (
        <Tooltip title = { tip }>
            <Tag { ...tagProps } />
        </Tooltip>
    )
}


function StatusLine() {
  const connectivity = useSelector(state => state.connectivity)
  const reachable = connectivity && connectivity.reachable
  const usb = connectivity && connectivity.usb
  const emu = connectivity && connectivity.emu

  let content

  if (reachable === null) {
    content = <span>Проверка подключения к локальному сервису...</span>
  } else if (reachable === false) {
    content = <Tag color="red">Локальный сервис недоступен</Tag>
  } else {
    content = (
      <>
        <TipTag color="green" tip = "Локальный сервис отвечает на запросы статуса.">Локальный сервис доступен</TipTag>
        { emu ? <TipTag color="blue" tip = "Работа в режиме эмуляции: датчики имитируются, USB-устройство не требуется.">Режим эмуляции</TipTag> :
               (usb ? <TipTag color="green" tip = "USB-устройство успешно открыто и готово к работе.">USB устройство подключено</TipTag> :
                      <TipTag color="orange" tip = "USB-устройство не открывается: оно не обнаружено или установлен несовместимый драйвер. Настройте драйвер по инструкции ниже.">USB устройство не найдено</TipTag>) }
        { connectivity.state ? <TipTag color="default" tip = { STATE_TOOLTIPS[connectivity.state] }>{ connectivity.state }</TipTag> : undefined }
        { connectivity.errorName ? <TipTag color="default" tip = { ERROR_TOOLTIPS[connectivity.errorName] }>{ connectivity.errorName }</TipTag> : undefined }
      </>
    )
  }

  return <div className="connectivity-status-line">{ content }</div>
}

function PortControl() {
  const [port, setPort] = useState(getLocalStorageValue(CONNECTIVITY_PORT_KEY, 8080))
  const [error, setError] = useState(null)

  function applyPort() {
    const value = Number(port)

    if (!Number.isInteger(value) || value < 1 || value > 65535) {
      setError('Некорректный порт. Укажите число от 1 до 65535.')
      return
    }

    setError(null)
    setLocalStorageValue(CONNECTIVITY_PORT_KEY, value)
    fetchStatus()
  }

  return (
    <div style={{ marginTop: 16 }}>
      <span>Порт локального сервиса</span>
      <InputNumber min={1} max={65535} value={port} onChange={setPort} style={{ marginLeft: 8, width: 120 }} />
      <Button style={{ marginLeft: 8 }} onClick={ applyPort }>Применить</Button>
      { error ? <div style={{ color: 'red', marginTop: 4 }}>{ error }</div> : undefined }
      <div style={{ color: 'gray', marginTop: 4 }}>
        Укажите порт, на котором запущен локальный сервис. Он настраивается вручную в конфигурационном файле сервиса.
      </div>
    </div>
  )
}

function deviceLabel(device) {
  const disambiguator = device.serial
    ? device.serial
    : (device.location ? `порт ${device.location}` : '')
  return disambiguator ? `${device.name} (${disambiguator})` : device.name
}

// User-facing names for the VID:PID pairs the local service recognizes
// (mirrors g_knownDevices in Usblib1Backend.cpp).
const KNOWN_USB_NAMES = {
  '0fcf:1004': 'ANTUSB Stick',
  '0fcf:1008': 'ANTUSB2 Stick',
  '0fcf:1009': 'ANTUSB-m Stick'
}

// Friendly label for a persisted device id that no longer matches the live
// list (e.g. a serial-less stick re-enumerated to a different port).
function staleDeviceLabel(id) {
  const parts = (id || '').split(':')
  if (parts.length < 3) return id
  const vidPid = `${parts[0]}:${parts[1]}`
  const name = KNOWN_USB_NAMES[vidPid] || vidPid
  const detail = parts[2] === 'S' ? `серийный № ${parts.slice(3).join(':')}` : `порт ${parts.slice(3).join('-')}`
  return `${name} (${detail}, изменился порт)`
}

function UsbDeviceSelect() {
  const connectivity = useSelector(state => state.connectivity)
  const devices = (connectivity && connectivity.devices) || []
  const configuredDevice = connectivity ? connectivity.configuredDevice : null
  const usb = connectivity ? connectivity.usb : null

  const [identifyOpen, setIdentifyOpen] = useState(false)
  const [identifyMessage, setIdentifyMessage] = useState(null)

  useEffect(() => {
    fetchUsbDevices()
  }, [])

  // Refresh the list when a stick is plugged in (detected by status polling).
  const prevUsb = useRef(usb)
  useEffect(() => {
    if (prevUsb.current === false && usb === true)
      fetchUsbDevices()
    prevUsb.current = usb
  }, [usb])

  function onDeviceChange(id) {
    const request = setUsbDevice(id || '')
    if (request && request.then) {
      request.then(() => fetchUsbDevices()).catch(() => fetchUsbDevices())
    } else {
      fetchUsbDevices()
    }
  }

  function identifyDevice() {
    setIdentifyMessage(null)

    fetchUsbDevices()
      .then(() => {
        const state = store.getState()
        const current = (state && state.connectivity && state.connectivity.devices) || []
        if (current.length === 0)
          setIdentifyMessage('Устройство не найдено. Подключите нужное устройство и повторите.')
        else if (current.length === 1) {
          const request = setUsbDevice(current[0].id)
          if (request && request.then) {
            request
              .then(() => {
                setIdentifyOpen(false)
                setIdentifyMessage(null)
                fetchUsbDevices()
              })
              .catch(() => setIdentifyMessage('Устройство не найдено. Подключите нужное устройство и повторите.'))
          } else {
            setIdentifyOpen(false)
            fetchUsbDevices()
          }
        } else
          setIdentifyMessage(`Осталось ${current.length} устройств. Выньте лишние и повторите.`)
      })
      .catch(() => setIdentifyMessage('Устройство не найдено. Подключите нужное устройство и повторите.'))
  }

  const shownConfigured = configuredDevice || ''
  const hasConfiguredOption = shownConfigured === '' || devices.some(device => device.id === shownConfigured)
  const stale = Boolean(shownConfigured) && !hasConfiguredOption

  return (
    <div style={{ marginTop: 16 }}>
      <div>USB устройство</div>
      <div style={{ marginTop: 8 }}>
        <Select value={ shownConfigured } onChange={ onDeviceChange } style={{ minWidth: 320 }}>
          <Select.Option value="">Автоматически (первое найденное)</Select.Option>
          { devices.map(device => (
            <Select.Option key={ device.id } value={ device.id } title={ device.description }>
              { deviceLabel(device) }
            </Select.Option>
          )) }
          { stale && (
            <Select.Option key={ shownConfigured } value={ shownConfigured } title = "Сохранённый выбор не совпадает с текущим устройством">
              { staleDeviceLabel(shownConfigured) }
            </Select.Option>
          ) }
        </Select>
        { !identifyOpen &&
          <Button style={{ marginLeft: 8 }} onClick={ () => { setIdentifyOpen(true); setIdentifyMessage(null) } }>
            Определить устройство
          </Button> }
      </div>
      { stale &&
        <div style={{ color: 'orange', marginTop: 4 }}>
          Сохранённое устройство изменило порт и не найдено в текущем списке. Переподключите адаптер,
          выберите его заново либо используйте «Автоматически» или «Определить устройство».
        </div> }
      { identifyOpen &&
        <div style={{ marginTop: 8 }}>
          <p>
            Выньте из компьютера все USB-устройства, кроме того, которое нужно выбрать,
            и нажмите «Обновить».
          </p>
          <Button size="small" onClick={ identifyDevice }>Обновить</Button>
          <Button size="small" style={{ marginLeft: 8 }} onClick={ () => { setIdentifyOpen(false); setIdentifyMessage(null) } }>
            Отмена
          </Button>
          { identifyMessage ? <div style={{ marginTop: 4 }}>{ identifyMessage }</div> : undefined }
        </div> }
    </div>
  )
}

function UsbDriverSetupNote() {
  const connectivity = useSelector(state => state.connectivity)
  const usb = connectivity && connectivity.usb

  // Auto-expand when a USB problem is likely, keep collapsed on success.
  const activeKey = usb === true ? [] : ['usb']

  return (
    <div style={{ marginTop: 16 }}>
      <Collapse defaultActiveKey = { activeKey }>
        <Collapse.Panel header = "Настройка USB-драйвера" key="usb">
          <p>
            Для доступа к ANT-адаптеру (Dynastream, VID <code>0fcf</code>), 
            требуется совместимый USB-драйвер. Если устройство видно в списке, но статус показывает
            ошибку (например, <code>DriverNotFound</code> или <code>DeviceNotFound</code>) —
            устройство присутствует, но его драйвер несовместим с libusb 1.0.
          </p>
          <h4>Windows</h4>
          <ol>
            <li>
              Скачайте и запустите <a href="https://zadig.akeo.ie/" target="_blank" rel="noreferrer">Zadig</a> —
              утилита установки драйверов USB (WinUSB / libusbK).
            </li>
            <li>В меню устройства выберите ANT-адаптер (VID <code>0fcf</code>, PID <code>1004</code>/<code>1008</code>/<code>1009</code>).</li>
            <li>Укажите целевой драйвер — <strong>WinUSB</strong> или <strong>libusbK</strong> (не libusb0/libusb-win32).</li>
            <li>Нажмите «Install Driver» / «Replace Driver».</li>
            <li>Проверьте в «Диспетчере устройств», что драйвер сменился на <code>WinUsb.sys</code> или <code>libusbK.sys</code>.</li>
            <li>Перезапустите connectivity сервис и заново выберите устройство.</li>
          </ol>
          <p>
            Библиотека libusb 1.0: <a href="https://libusb.info/" target="_blank" rel="noreferrer">libusb.info</a>.
          </p>
          <h4>Linux</h4>
          <p>
            Драйвер устанавливать не нужно — достаточно libusb (обычно уже установлен).
            Проблема, как правило, в правах доступа: сервису нужно разрешение на устройство.
            Создайте правило udev <code>/etc/udev/rules.d/50-ant.rules</code>:
          </p>
          <pre>{`SUBSYSTEM=="usb", ATTR{idVendor}=="0fcf", ATTR{idProduct}=="1004", MODE="0666", GROUP="plugdev"
SUBSYSTEM=="usb", ATTR{idVendor}=="0fcf", ATTR{idProduct}=="1008", MODE="0666", GROUP="plugdev"
SUBSYSTEM=="usb", ATTR{idVendor}=="0fcf", ATTR{idProduct}=="1009", MODE="0666", GROUP="plugdev"`}</pre>
          <p>
            Затем примените правило и добавьте пользователя в группу:
          </p>
          <pre>{`sudo udevadm control --reload-rules
sudo udevadm trigger
sudo usermod -aG plugdev $USER`}</pre>
          <p>
            Перезайдите в систему и перезапустите connectivity сервис.
          </p>
        </Collapse.Panel>
      </Collapse>
    </div>
  )
}

function PanelLocalServer() {
  const connectivity = useSelector(state => state.connectivity)
  const reachable = connectivity && connectivity.reachable
  const emu = connectivity && connectivity.emu
  const blocked = isTrainingBlocked(connectivity)

  useEffect(() => {
    startStatusPolling(5000)
    checkDaemonCompatibility()
    return () => stopStatusPolling()
  }, [])

  return (
    <div>
      <StatusLine/>
      { blocked ? <DaemonBlockBanner daemonApi = { connectivity.daemonApi }/> : undefined }
      <Button style={{ marginTop: 16 }} onClick={ fetchStatus }>Проверить</Button>
      <PortControl/>
      { (reachable && !emu) ? <UsbDeviceSelect/> : undefined }
      { (reachable && !emu) ? <UsbDriverSetupNote/> : undefined }
      <p style={{ marginTop: 8 }}>Чтобы использовать датчики, скачайте и запустите локальный сервис.</p>
      <DaemonDownloadControl/>
    </div>
  )
}

export default PanelLocalServer